import React, { useState, useEffect } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import type { AdminTransaction } from '../../services/adminApiService';
import './AdminComponents.css';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: AdminTransaction | null;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'security' | 'timeline'>('details');

  if (!isOpen || !transaction) {
    return null;
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    let safeDateString = dateString.replace(/\.\d+/, '');
    if (!safeDateString.endsWith('Z')) {
      safeDateString += 'Z';
    }
    const date = new Date(safeDateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'status-badge approved';
      case 'PENDING': return 'status-badge pending';
      case 'FAILED': return 'status-badge rejected';
      case 'CANCELLED': return 'status-badge inactive';
      default: return 'status-badge';
    }
  };

  const getTransactionTypeIcon = (type: string) => {
    switch (type) {
      case 'CREDIT': return '⬇️';
      case 'DEBIT': return '⬆️';
      case 'TRANSFER': return '↔️';
      case 'PAYMENT': return '💳';
      default: return '💰';
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'CREDIT': return '#059669';
      case 'DEBIT': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const formatAmount = (amount: number, type: string) => {
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const sign = type === 'CREDIT' ? '+' : type === 'DEBIT' ? '-' : '';
    return `${sign}$${formatted}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content transaction-details-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="transaction-header">
            <div className="transaction-icon">
              <span style={{ fontSize: '32px' }}>{getTransactionTypeIcon(transaction.type)}</span>
            </div>
            <div className="transaction-title">
              <h2>Transaction #{transaction.id}</h2>
              <div className="transaction-meta">
                <span className={getStatusBadgeClass(transaction.status)}>
                  {transaction.status}
                </span>
                <span className="transaction-category">{transaction.category}</span>
              </div>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button
            className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            📋 Transaction Details
          </button>
          <button
            className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            🔒 Security & Risk
          </button>
          <button
            className={`tab-btn ${activeTab === 'timeline' ? 'active' : ''}`}
            onClick={() => setActiveTab('timeline')}
          >
            ⏱️ Timeline
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          {activeTab === 'details' && (
            <div className="transaction-details">
              {/* Amount Section */}
              <div className="amount-section">
                <div className="amount-display" style={{ color: getTransactionTypeColor(transaction.type) }}>
                  {formatAmount(transaction.amount, transaction.type)}
                </div>
                <div className="amount-label">
                  {transaction.type === 'CREDIT' ? 'Received' : transaction.type === 'DEBIT' ? 'Sent' : 'Transaction'}
                </div>
              </div>

              {/* Customer Information */}
              <div className="info-section">
                <h3>Customer Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Customer Name</label>
                    <span>{transaction.customerName}</span>
                  </div>
                  <div className="info-item">
                    <label>Customer Email</label>
                    <span>{transaction.customerEmail}</span>
                  </div>
                  <div className="info-item">
                    <label>Customer ID</label>
                    <span>{transaction.customerId}</span>
                  </div>
                </div>
              </div>

              {/* Account Information */}
              <div className="info-section">
                <h3>Account Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Account ID</label>
                    <span>{transaction.accountId}</span>
                  </div>
                  <div className="info-item">
                    <label>Account Number</label>
                    <span style={{ fontFamily: 'monospace' }}>{transaction.accountNumber}</span>
                  </div>
                </div>
              </div>

              {/* Transaction Information */}
              <div className="info-section">
                <h3>Transaction Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Transaction ID</label>
                    <span>{transaction.transactionId || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Type</label>
                    <span>{transaction.type}</span>
                  </div>
                  <div className="info-item">
                    <label>Category</label>
                    <span>{transaction.category}</span>
                  </div>
                  <div className="info-item">
                    <label>Description</label>
                    <span>{transaction.description || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Currency</label>
                    <span>{transaction.currency || 'USD'}</span>
                  </div>
                  <div className="info-item">
                    <label>Reference</label>
                    <span>{transaction.reference || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              {(transaction.recipientAccount || transaction.recipientName || transaction.paymentMethod) && (
                <div className="info-section">
                  <h3>Payment Details</h3>
                  <div className="info-grid">
                    {transaction.recipientAccount && (
                      <div className="info-item">
                        <label>Recipient Account</label>
                        <span style={{ fontFamily: 'monospace' }}>{transaction.recipientAccount}</span>
                      </div>
                    )}
                    {transaction.recipientName && (
                      <div className="info-item">
                        <label>Recipient Name</label>
                        <span>{transaction.recipientName}</span>
                      </div>
                    )}
                    {transaction.paymentMethod && (
                      <div className="info-item">
                        <label>Payment Method</label>
                        <span>{transaction.paymentMethod}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'security' && (
            <div className="security-details">
              <div className="info-section">
                <h3>Security & Risk Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Suspicious Activity</label>
                    <span className={transaction.isSuspicious ? 'status-badge rejected' : 'status-badge approved'}>
                      {transaction.isSuspicious ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Risk Score</label>
                    <span>{transaction.riskScore || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Requires Approval</label>
                    <span className={transaction.requiresApproval ? 'status-badge pending' : 'status-badge approved'}>
                      {transaction.requiresApproval ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>IP Address</label>
                    <span>{transaction.ipAddress || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Device Fingerprint</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {transaction.deviceFingerprint || 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Reversible</label>
                    <span className={transaction.isReversible ? 'status-badge pending' : 'status-badge approved'}>
                      {transaction.isReversible ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Balance Information */}
              <div className="info-section">
                <h3>Balance Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Balance Before</label>
                    <span>{transaction.balanceBefore ? `$${transaction.balanceBefore.toLocaleString()}` : 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Balance After</label>
                    <span>{transaction.balanceAfter ? `$${transaction.balanceAfter.toLocaleString()}` : 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="timeline-details">
              <div className="info-section">
                <h3>Transaction Timeline</h3>
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-icon">📅</div>
                    <div className="timeline-content">
                      <div className="timeline-title">Transaction Created</div>
                      <div className="timeline-date">{formatDate(transaction.transactionDate)}</div>
                    </div>
                  </div>
                  
                  {transaction.approvedAt && (
                    <div className="timeline-item">
                      <div className="timeline-icon">✅</div>
                      <div className="timeline-content">
                        <div className="timeline-title">Transaction Approved</div>
                        <div className="timeline-date">{formatDate(transaction.approvedAt)}</div>
                        {transaction.approvedBy && (
                          <div className="timeline-subtitle">Approved by: {transaction.approvedBy}</div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {transaction.reversedAt && (
                    <div className="timeline-item">
                      <div className="timeline-icon">🔄</div>
                      <div className="timeline-content">
                        <div className="timeline-title">Transaction Reversed</div>
                        <div className="timeline-date">{formatDate(transaction.reversedAt)}</div>
                        {transaction.reversedBy && (
                          <div className="timeline-subtitle">Reversed by: {transaction.reversedBy}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Approval Information */}
              {(transaction.approvedBy || transaction.rejectionReason) && (
                <div className="info-section">
                  <h3>Approval Information</h3>
                  <div className="info-grid">
                    {transaction.approvedBy && (
                      <div className="info-item">
                        <label>Approved By</label>
                        <span>{transaction.approvedBy}</span>
                      </div>
                    )}
                    {transaction.approvedAt && (
                      <div className="info-item">
                        <label>Approved At</label>
                        <span>{formatDate(transaction.approvedAt)}</span>
                      </div>
                    )}
                    {transaction.rejectionReason && (
                      <div className="info-item">
                        <label>Rejection Reason</label>
                        <span style={{ color: '#dc2626' }}>{transaction.rejectionReason}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="modal-btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal; 